/**
 * BrandingProvider.
 *
 * Reads the tenant's manifest.branding and applies it to the page:
 * - Sets brand colour CSS variables via shared brandingCss helper
 * - Updates document.title with tenant name
 * - Switches favicon if specified
 *
 * Wrap the app in this provider so any tenant context change reflows
 * the visual identity. With white-labeling, a tenant should never see
 * "TNN" branding in their workspace; this provider is what enforces that.
 */
import { useEffect, type ReactNode } from "react";

import { applyBrandingCss } from "@/features/branding/brandingCss";
import { productDisplayName } from "@/features/manifest/lib/productDisplayName";

import { useManifest } from "../model/useManifest";


interface Props {
  children: ReactNode;
}

/** Applies the tenant's manifest branding (brand colour, logo) to the page. */
export function BrandingProvider({ children }: Props) {
  const { manifest } = useManifest();

  useEffect(() => {
    if (!manifest) return;
    const { branding } = manifest;

    applyBrandingCss(branding);

    const dashboardName = productDisplayName(manifest);
    document.title = dashboardName ? `${dashboardName}` : "TNN Ops";

    if (branding?.favicon_url) {
      const link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
      if (link) link.href = branding.favicon_url;
    }
  }, [manifest]);

  return <>{children}</>;
}
